import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiEye, FiEyeOff } from "react-icons/fi";
import ButtonLoader from "../../components/common/ButtonLoader";
import { persistAuthSession } from "../../utils/auth";
import { apiClient } from "../../config/api";

const Login = () => {
  const navigate = useNavigate();
  const [phoneNumber, setPhoneNumber] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({ phoneNumber: "", password: "" });
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState("");

  const validateForm = () => {
    let valid = true;
    const newErrors = { phoneNumber: "", password: "" };

    if (!phoneNumber) {
      newErrors.phoneNumber = "Phone number is required.";
      valid = false;
    } else if (!/^\d{10}$/.test(phoneNumber)) {
      newErrors.phoneNumber = "Phone number must be 10 digits.";
      valid = false;
    }

    if (!password) {
      newErrors.password = "Password is required.";
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  const handleLogin = async () => {
    setApiError("");
    if (validateForm()) {
      setIsLoading(true);
      try {
        const payload = {
          phone: phoneNumber,
          password: password,
        };
        const response = await apiClient.post("/users/login", payload);

        const { token, user } = response.data;
        persistAuthSession({ token, user });
        navigate("/home");
      } catch (error) {
        console.error("Login error:", error);
        if (error.response && error.response.data && error.response.data.message) {
          setApiError(error.response.data.message);
        } else if (error.message) {
          setApiError(error.message);
        } else {
          setApiError("Login failed. Please check your credentials or try again later.");
        }
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <div className="min-h-screen bg-[#0e0e0e] flex items-center justify-center px-4">
      <div className="w-full max-w-lg bg-[#161616] border border-[#2a2618] rounded-xl shadow-2xl p-10">
        <div className="w-12 h-0.5 bg-[#d4a017] mx-auto mb-6 rounded-full"></div>

        <h1 className="text-center text-2xl font-light tracking-wide text-[#f0e8d0] mb-1">
          Agent Portal
        </h1>
        <p className="text-center text-xs font-mono tracking-wider text-[#7a6a50] mb-8">
          SECURE_AUTHENTICATION_REQUIRED
        </p>

        <div className="space-y-5">
          <div>
            <label className="block text-xs font-mono tracking-wider text-[#a09070] mb-1">
              PHONE NUMBER
            </label>
            <input
              type="text"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              className={`mt-1 block w-full px-4 py-2.5 bg-[#0a0a0a] border ${
                errors.phoneNumber ? "border-red-500" : "border-[#2a2618]"
              } rounded-md text-[#e8e0cc] text-sm font-mono placeholder:text-[#3a3520] focus:outline-none focus:ring-1 focus:ring-[#d4a017] focus:border-[#d4a017] transition-all`}
              placeholder="ENTER 10 DIGIT NUMBER"
            />
            {errors.phoneNumber && (
              <p className="text-xs text-red-400 mt-1 font-mono">{errors.phoneNumber}</p>
            )}
          </div>

          <div>
            <label className="block text-xs font-mono tracking-wider text-[#a09070] mb-1">
              PASSWORD
            </label>
            <div className="relative mt-1">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={`block w-full px-4 py-2.5 pr-12 bg-[#0a0a0a] border ${
                  errors.password ? "border-red-500" : "border-[#2a2618]"
                } rounded-md text-[#e8e0cc] text-sm font-mono placeholder:text-[#3a3520] focus:outline-none focus:ring-1 focus:ring-[#d4a017] focus:border-[#d4a017] transition-all`}
                placeholder="********"
              />
              <button
                type="button"
                onClick={() => setShowPassword((visible) => !visible)}
                className="absolute inset-y-0 right-0 flex items-center justify-center px-4 text-[#7a6a50] hover:text-[#d4a017] transition-colors"
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
              </button>
            </div>
            {errors.password && (
              <p className="text-xs text-red-400 mt-1 font-mono">{errors.password}</p>
            )}
          </div>

          <div className="pt-2">
            <button
              onClick={handleLogin}
              disabled={isLoading}
              className="w-full h-12 flex justify-center items-center bg-[#d4a017] text-[#0e0e0e] font-bold tracking-widest text-sm rounded-md transition-all hover:bg-[#e5b420] focus:ring-2 focus:ring-[#d4a017] focus:ring-offset-2 focus:ring-offset-[#161616] disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {isLoading ? <ButtonLoader /> : "AUTHENTICATE ->"}
            </button>
            {apiError && (
              <p className="text-xs text-red-400 mt-2 text-center font-mono">{apiError}</p>
            )}
          </div>
        </div>

        <div className="mt-6 text-center">
          <span className="inline-flex items-center gap-1.5 text-[10px] font-mono text-[#5a5040] tracking-wider">
            <span className="w-1.5 h-1.5 rounded-full bg-[#22c55e] animate-pulse"></span>
            SECURE_ENCRYPTED_CHANNEL // AES-256
          </span>
        </div>
      </div>
    </div>
  );
};

export default Login;
